import { useCallback, useEffect, useState } from "react";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrivilege, type SasTokenPrefix } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { t } from "i18next";

const LANG_BASE = "vessel-call.create";
const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

type UploadVariables = {
  files: File[];
};

const toDocumentEntry = (entry: UploadEntry): Record<string, unknown> => {
  if (entry instanceof File) {
    return {
      name: entry.name,
      type: entry.type,
      url: undefined,
    };
  }

  return (entry as Record<string, unknown>) ?? {};
};

export const useDepartureUploadAdditionalDocuments = (
  field: string,
  token: SasTokenPrefix,
  formData?: any,
  formStore?: any,
) => {
  const store = useUploadStore.getState();
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const updateFilesForKey = useUploadStore((state) => state.updateFilesForKey);
  const uploadEntries = useUploadStore((state) => state.filesByKey[field] ?? EMPTY_UPLOAD_ENTRIES);
  const [uploadingIndexes, setUploadingIndexes] = useState<number[]>([]);

  const { notify } = useNotification();

  const setUploadEntries = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) => {
      if (typeof next === "function") {
        updateFilesForKey(field, next as (prev: UploadEntry[]) => UploadEntry[]);
        return;
      }

      setFilesForKey(field, next, { mode: "replace" });
    },
    [field, setFilesForKey, updateFilesForKey],
  );

  const setFormDocument = useCallback(
    (docs: UploadEntry[]) => {
      formStore?.setState((state: any) => ({
        ...state,
        [field]: docs ? docs : [],
      }));
    },
    [field],
  );

  const setDocumentValue = useCallback(
    (docs: UploadEntry[]) => {
      if (!formData?.setValue) return;

      const isSubmitted = formData?.formState?.isSubmitted;
      formData.setValue(field, docs, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: Boolean(isSubmitted),
      });
    },
    [formData, field],
  );

  const mergeWithCurrentFormDocuments = useCallback(
    (docs: UploadEntry[]) => {
      if (!formData?.getValues) return docs;

      const currentFormDocs = Array.isArray(formData.getValues(field))
        ? (formData.getValues(field) as Array<Record<string, unknown>>)
        : [];

      return docs.map((doc, index) => {
        const current = currentFormDocs[index] ?? {};
        const next = toDocumentEntry(doc);

        return {
          ...current,
          name: (next.name as string | undefined) ?? (current.name as string | undefined) ?? "",
          url: (next.url as string | undefined) ?? (current.url as string | undefined) ?? "",
          type: (next.type as string | undefined) ?? (current.type as string | undefined) ?? "",
        } as UploadEntry;
      });
    },
    [field, formData],
  );

  const clearDocumentValue = useCallback(() => {
    setDocumentValue([]);
    if (formData?.trigger && formData?.formState?.isSubmitted) {
      void formData.trigger(field);
    }
  }, [setDocumentValue, formData, field]);

  useEffect(() => {
    if (!formData) return;

    const currentFormValue = formData.getValues?.(field);
    const mergedEntries = mergeWithCurrentFormDocuments(uploadEntries);
    const isDifferent = JSON.stringify(currentFormValue) !== JSON.stringify(mergedEntries);

    if (uploadEntries.length && isDifferent) {
      setDocumentValue(mergedEntries);
    }
  }, [uploadEntries, setDocumentValue, formData, field, mergeWithCurrentFormDocuments]);

  const uploadDocuments = useCallback(
    ({ files }: UploadVariables): Promise<UploadEntry[]> =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "DepartureRequest",
          action: "upload_departure_document",
          entityType: "departure_document",
        },
        async (transactionId) => {
          if (!files.length) return [];

          const uploadedEntries: UploadEntry[] = [];

          for (const file of files) {
            const payloadForSasToken = {
              prefix: token,
              contentType: file.type || null,
              privilege: SasTokenPrivilege.WRITE,
              blobUrl: null,
            };

            const { blobUrl, sasToken } = await getSasToken(
              payloadForSasToken,
              ...(transactionId ? [transactionId] : []),
            );

            const [uploadResult] = await uploadFileToAzureBlob({
              file,
              blobUrl,
              sasToken,
              certificateType: "ADDITIONAL_DOCUMENT",
            });

            const documentUrl = uploadResult?.documentUrl ?? blobUrl;

            if (!documentUrl) continue;

            uploadedEntries.push({
              name: file.name,
              url: documentUrl,
              type: file.type,
            });
          }

          return uploadedEntries;
        },
      ),
    [field, token],
  );

  const handleUploadError = useCallback(() => {
    notify({
      title: t(`${LANG_BASE}.notifications.vessel-call-upload-document-error-title`),
      description: t(`${LANG_BASE}.notifications.vessel-call-upload-document-error-description`),
      type: "error",
    });

    store.clearFilesForKey(field);
    setUploadEntries([]);
    setFormDocument([]);
    if (formData) clearDocumentValue();
  }, [clearDocumentValue, field, formData, notify, setFormDocument, store]);

  const getLastPendingFileIndexes = (entries: UploadEntry[], count: number) => {
    const pendingIndexes = entries
      .map((entry, index) => (entry instanceof File ? index : -1))
      .filter((index) => index >= 0);

    return pendingIndexes.slice(-count);
  };

  const removeUploadingIndexes = (currentIndexes: number[], indexesToRemove: number[]) => {
    const nextIndexes = [...currentIndexes];

    indexesToRemove.forEach((uploadingIndex) => {
      const index = nextIndexes.indexOf(uploadingIndex);
      if (index >= 0) {
        nextIndexes.splice(index, 1);
      }
    });

    return nextIndexes;
  };

  const uploadMutation = useMutation({
    mutationFn: uploadDocuments,
    onMutate: ({ files }) => {
      const persistedEntries = store.getFilesForKey(field) ?? [];
      const pendingIndexes = getLastPendingFileIndexes(persistedEntries, files.length);
      setUploadingIndexes((currentIndexes) => [...currentIndexes, ...pendingIndexes]);
      return { pendingIndexes };
    },
    onSuccess: (result) => {
      if (!result.length) return;

      const persistedEntries = store.getFilesForKey(field) ?? [];
      const uploadedEntries = persistedEntries.filter((entry): entry is UploadEntry => {
        return !(entry instanceof File);
      });

      const nextEntries = [...uploadedEntries, ...result];
      const mergedEntries = mergeWithCurrentFormDocuments(nextEntries);
      setUploadEntries(nextEntries);
      setFormDocument(mergedEntries);

      if (formData) setDocumentValue(mergedEntries);
    },
    onError: () => {
      handleUploadError();
    },
    onSettled: (_data, _error, _variables, context) => {
      setUploadingIndexes((currentIndexes) =>
        removeUploadingIndexes(currentIndexes, context?.pendingIndexes ?? []),
      );
    },
  });

  const handleUpload = useCallback(
    (files: File[]) => {
      if (!files.length) return;
      uploadMutation.mutate({ files });
    },
    [uploadMutation],
  );

  const handleRemove = useCallback(
    (index?: number) => {
      if (typeof index !== "number") {
        store.clearFilesForKey(field);
        setFormDocument([]);
        setUploadEntries([]);
        if (formData) setDocumentValue([]);
        return;
      }

      const currentEntries = formData?.getValues?.(field);
      const updatedEntries = Array.isArray(currentEntries)
        ? currentEntries.filter((_: unknown, idx: number) => idx !== index)
        : [];

      setUploadEntries(updatedEntries);
      setFormDocument(updatedEntries);
      if (formData) {
        setDocumentValue(updatedEntries);
        formData.clearErrors(field);
        if (formData.formState?.isSubmitted) {
          void formData.trigger(field);
        }
      }
    },
    [field, formData, setDocumentValue, setFormDocument, setUploadEntries, store],
  );

  return {
    uploadEntries,
    setUploadEntries,
    handleUpload,
    handleRemove,
    uploadingIndexes,
    notSuccess: uploadMutation.isPending || uploadMutation.isIdle,
    isPending: uploadMutation.isPending,
  };
};
